import React, { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { cn } from "~/lib/cn";
import { Input } from "~/components/ui/input";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { ControlledRadioGroup } from "~/components/form/controlled-inputs";
import { CountrySelect } from "~/components/country-select";
import { useDiscoveryForm } from "~/lib/hooks/use-discovery-form";
import { createNoIndexMeta } from "~/lib/shared/meta";

import {
  FormOfConductSchema,
  coachLocationCountryField,
  coachLocationRegionField,
  formOfConductStep,
  prefersCoachLocationField,
  sessionDeliveryMethodsFeild,
} from "~/lib/schemas/coach-discovery";

import type { FormOfConductForm } from "~/lib/schemas/coach-discovery";

export const Route = createFileRoute("/_public/discovery/steps/7/")({
  head: () =>
    createNoIndexMeta(
      "Step 7: Session Delivery & Location – Dream Coach Match",
      "Set your session delivery method and location preferences in our discovery process",
    ),
  component: DiscoveryStep7,
});

function DiscoveryStep7() {
  const { form, handleNextStep, handlePreviousStep } = useDiscoveryForm(
    FormOfConductSchema,
    7,
  );
  const { control, handleSubmit, watch, setValue, formState } = form;
  const { errors, isSubmitting, isValid } = formState;

  const watchedValues = watch();
  const prefersCoachLocation = watchedValues.prefersCoachLocation;
  const sessionDeliveryMethods = watchedValues.sessionDeliveryMethods;

  const _validateCountry = (value: string | undefined) => {
    if ("Yes" === prefersCoachLocation && !value) {
      return "Please select your country if you selected 'Yes'.";
    }
    return true;
  };

  const onSubmit = (_data: FormOfConductForm) => {
    handleNextStep();
  };

  // Handle automatic setting of prefersCoachLocation when sessionDeliveryMethods changes
  useEffect(() => {
    if (
      "In-person" === sessionDeliveryMethods ||
      "Hybrid" === sessionDeliveryMethods
    ) {
      if ("No preference" === prefersCoachLocation) {
        setValue("prefersCoachLocation", "Yes");
      }
    } else if ("Online" === sessionDeliveryMethods) {
      if (
        "Yes" === prefersCoachLocation &&
        !watchedValues.coachLocationCountry
      ) {
        setValue("prefersCoachLocation", "No preference");
      }
    }
  }, [
    sessionDeliveryMethods,
    prefersCoachLocation,
    watchedValues.coachLocationCountry,
    setValue,
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title={formOfConductStep.title} />
      <DiscoveryStep.Card>
        <FormField
          label={sessionDeliveryMethodsFeild.subTitle || ""}
          labelSize="xl"
          error={errors.sessionDeliveryMethods?.message}
        >
          <ControlledRadioGroup
            control={control}
            name="sessionDeliveryMethods"
            options={
              sessionDeliveryMethodsFeild.options?.map(({ value, label }) => ({
                value,
                label,
              })) || []
            }
            className="flex flex-col gap-y-4"
          />
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Card className="mt-6 p-8">
        <FormField
          labelSize="xl"
          error={errors.prefersCoachLocation?.message}
          label={prefersCoachLocationField.subTitle}
        >
          <div className="mt-4 flex flex-col gap-y-6">
            <ControlledRadioGroup
              control={control}
              name="prefersCoachLocation"
              options={
                prefersCoachLocationField.options?.map(({ value, label }) => ({
                  value,
                  label,
                })) || []
              }
              className="flex flex-col gap-y-6"
            />

            {/* Conditional Location Details */}
            <div
              className={cn(
                "max-h-0 overflow-hidden pl-6 opacity-0 transition-all duration-700",
                {
                  "max-h-screen opacity-100":
                    prefersCoachLocation?.includes("Yes"),
                },
              )}
            >
              <div className="mt-4 flex flex-col gap-4">
                <FormField
                  error={errors.coachLocationCountry?.message}
                  label={coachLocationCountryField.subTitle}
                >
                  <CountrySelect
                    value={watchedValues.coachLocationCountry}
                    onChange={(value) => {
                      setValue("coachLocationCountry", value, {
                        shouldValidate: true,
                      });
                    }}
                  />
                </FormField>

                <FormField
                  label={coachLocationRegionField.subTitle || ""}
                  error={errors.coachLocationRegion?.message}
                >
                  <Input
                    value={watchedValues.coachLocationRegion || ""}
                    onChange={(e) =>
                      setValue("coachLocationRegion", e.target.value)
                    }
                    error={!!errors.coachLocationRegion?.message}
                  />
                </FormField>
              </div>
            </div>
          </div>
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Control
        prev={handlePreviousStep}
        type="submit"
        disabled={!isValid}
        loading={isSubmitting}
      />
    </form>
  );
}
